import type React from "react";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {addQuotaChange, getQuotaChanges, getQuotaUsage} from "../../api/tenantDetailApi";
import type {QuotaChange, QuotaChangeCreateRequest, QuotaType, QuotaUsageEvent} from "../../types/tenant";
import {formatDateTime} from "../../util/format";

const BYTES_PER_MEGABYTE = 1024 * 1024;

/**
 * Every value the backend's SMS producers write. It types `messageType` as a free-form string, so
 * anything added later still renders as itself rather than as an empty cell.
 */
const MESSAGE_TYPE_KEYS: Record<string, string> = {
    TEST: "DETAIL_MESSAGE_TYPE_TEST",
    APPOINTMENT_REMINDER: "DETAIL_MESSAGE_TYPE_APPOINTMENT_REMINDER",
    APPOINTMENT_CANCELLATION: "DETAIL_MESSAGE_TYPE_APPOINTMENT_CANCELLATION",
    APPOINTMENT_UPDATE: "DETAIL_MESSAGE_TYPE_APPOINTMENT_UPDATE",
    BOOKING_CONFIRMATION: "DETAIL_MESSAGE_TYPE_BOOKING_CONFIRMATION",
    BULK_SMS: "DETAIL_MESSAGE_TYPE_BULK_SMS",
};

export function quotaTypeLabelKey(quota: QuotaType): string {
    if (quota === "sms") return "DETAIL_QUOTA_SMS";
    if (quota === "email") return "DETAIL_QUOTA_EMAIL";
    return "DETAIL_QUOTA_STORAGE_TYPE";
}

function QuotaChangeHistory({tenantId}: {tenantId: string}) {
    const {t} = useTranslation();
    const [changes, setChanges] = useState<QuotaChange[] | null>(null);
    const [err, setErr] = useState<string | null>(null);

    // `t` is deliberately not a dependency: its identity changes across renders, which would refetch
    // on every one of them.
    useEffect(() => {
        let cancelled = false;
        getQuotaChanges(tenantId)
                .then((loaded) => {
                    if (!cancelled) setChanges(loaded);
                })
                .catch((e: unknown) => {
                    if (!cancelled) setErr(e instanceof Error ? e.message : "");
                });
        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    if (err != null) {
        return (
                <Alert severity="error" sx={{mb: 2}}>
                    {err ? `${t("DETAIL_QUOTA_CHANGES_ERROR")}: ${err}` : t("DETAIL_QUOTA_CHANGES_ERROR")}
                </Alert>
        );
    }

    if (changes == null) {
        return (
                <Typography variant="body2" sx={{mb: 2}}>
                    {t("DETAIL_QUOTA_CHANGES_LOADING")}
                </Typography>
        );
    }

    return (
            <Box sx={{mb: 2}}>
                <Typography variant="h6" component="h3" gutterBottom>
                    {t("DETAIL_QUOTA_CHANGES")}
                </Typography>
                {changes.length === 0 ? (
                        <Typography variant="body2">{t("DETAIL_QUOTA_CHANGES_EMPTY")}</Typography>
                ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{maxHeight: 260}}>
                            <Table size="small" stickyHeader aria-label={t("DETAIL_QUOTA_CHANGES")}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("DETAIL_QUOTA_TIMESTAMP")}</TableCell>
                                        <TableCell>{t("DETAIL_QUOTA_TYPE")}</TableCell>
                                        <TableCell align="right">{t("DETAIL_QUOTA_AMOUNT")}</TableCell>
                                        <TableCell>{t("DETAIL_QUOTA_NOTE")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {changes.map((change) => (
                                            <TableRow key={change.id}>
                                                <TableCell>{formatDateTime(change.createdAt)}</TableCell>
                                                <TableCell>{t(quotaTypeLabelKey(change.quota))}</TableCell>
                                                <TableCell align="right">
                                                    {change.quota === "storageBytes"
                                                            ? t("DETAIL_QUOTA_AMOUNT_IN_MEGABYTES", {
                                                                amount: change.amount / BYTES_PER_MEGABYTE,
                                                            })
                                                            : change.amount}
                                                </TableCell>
                                                <TableCell>{change.note ?? ""}</TableCell>
                                            </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                )}
            </Box>
    );
}

export function QuotaUsageDialogContent({tenantId, onClose}: {tenantId: string; onClose: () => void}) {
    const {t} = useTranslation();
    const [events, setEvents] = useState<QuotaUsageEvent[] | null>(null);
    const [err, setErr] = useState<string | null>(null);

    // `t` is deliberately not a dependency - see QuotaChangeHistory.
    useEffect(() => {
        let cancelled = false;
        getQuotaUsage(tenantId)
                .then((loaded) => {
                    if (!cancelled) setEvents(loaded);
                })
                .catch((e: unknown) => {
                    if (!cancelled) setErr(e instanceof Error ? e.message : "");
                });
        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    return (
            <>
                <DialogTitle>{t("DETAIL_QUOTA_USAGE_REPORT")}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{mb: 2}}>
                        {t("DETAIL_QUOTA_USAGE_SMS_ONLY")}
                    </Alert>
                    {err != null && (
                            <Alert severity="error" sx={{mb: 2}}>
                                {err ? `${t("DETAIL_QUOTA_USAGE_ERROR")}: ${err}` : t("DETAIL_QUOTA_USAGE_ERROR")}
                            </Alert>
                    )}
                    {err == null && events == null && <Typography variant="body2">{t("DETAIL_QUOTA_USAGE_LOADING")}</Typography>}
                    {events != null && events.length === 0 && <Typography variant="body2">{t("DETAIL_QUOTA_USAGE_EMPTY")}</Typography>}
                    {events != null && events.length > 0 && (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small" aria-label={t("DETAIL_QUOTA_USAGE_REPORT")}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("DETAIL_QUOTA_TIMESTAMP")}</TableCell>
                                            <TableCell>{t("DETAIL_QUOTA_USAGE_PHONE_NUMBER")}</TableCell>
                                            <TableCell>{t("DETAIL_QUOTA_USAGE_MESSAGE_TYPE")}</TableCell>
                                            <TableCell align="right">{t("DETAIL_QUOTA_USAGE_MESSAGE_LENGTH")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {events.map((event) => (
                                                <TableRow key={event.id}>
                                                    <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                                                    <TableCell>{event.phoneNumber}</TableCell>
                                                    <TableCell>
                                                        {MESSAGE_TYPE_KEYS[event.messageType] ? t(MESSAGE_TYPE_KEYS[event.messageType]) : event.messageType}
                                                    </TableCell>
                                                    <TableCell align="right">{event.messageLength}</TableCell>
                                                </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>{t("CLOSE")}</Button>
                </DialogActions>
            </>
    );
}

export function QuotaEditDialogContent({tenantId, onClose, onSaved}: {tenantId: string; onClose: () => void; onSaved: () => void}) {
    const {t} = useTranslation();
    const [quota, setQuota] = useState<QuotaType>("sms");
    const [amount, setAmount] = useState(0);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // Storage is entered in megabytes: a raw-byte field made "500" mean 500 bytes.
    const entersMegabytes = quota === "storageBytes";

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null);
        setSubmitting(true);
        try {
            const body: QuotaChangeCreateRequest = {
                quota,
                amount: entersMegabytes ? amount * BYTES_PER_MEGABYTE : amount,
                note: note.trim() || undefined,
            };
            await addQuotaChange(tenantId, body);
            onSaved();
        } catch (e) {
            setErr(e instanceof Error ? e.message : null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
            <>
                <DialogTitle>{t("DETAIL_ADD_QUOTA_CHANGE")}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <QuotaChangeHistory tenantId={tenantId} />
                        {err != null && (
                                <Alert severity="error" sx={{mb: 2}}>
                                    {err}
                                </Alert>
                        )}
                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "160px 1fr"}, gap: 2}}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="quota-type-label">{t("DETAIL_QUOTA_TYPE")}</InputLabel>
                                <Select
                                        labelId="quota-type-label"
                                        value={quota}
                                        label={t("DETAIL_QUOTA_TYPE")}
                                        onChange={(e) => setQuota(e.target.value as QuotaType)}
                                >
                                    <MenuItem value="sms">{t("DETAIL_QUOTA_SMS")}</MenuItem>
                                    <MenuItem value="storageBytes">{t("DETAIL_QUOTA_STORAGE_TYPE")}</MenuItem>
                                    <MenuItem value="email">{t("DETAIL_QUOTA_EMAIL")}</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                    label={entersMegabytes ? t("DETAIL_AMOUNT_DELTA_MEGABYTES") : t("DETAIL_AMOUNT_DELTA")}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={amount}
                                    onChange={(e) => setAmount(Number(e.target.value))}
                            />
                            <TextField
                                    label={t("DETAIL_NOTE_OPTIONAL")}
                                    size="small"
                                    fullWidth
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    slotProps={{htmlInput: {maxLength: 256}}}
                                    sx={{gridColumn: {sm: "1 / -1"}}}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={onClose}>{t("CANCEL")}</Button>
                        <Button type="submit" variant="contained" disabled={submitting}>
                            {submitting ? t("DETAIL_SAVING") : t("DETAIL_ADD_CHANGE")}
                        </Button>
                    </DialogActions>
                </form>
            </>
    );
}
